import React, { useState, useEffect, useRef, useReducer, Fragment } from 'react';
import classnames from 'classnames';
import { fabric } from 'fabric';
import MatomoTracker from '@datapunt/matomo-tracker-js';
import Zuploader from 'zuploader';
import { ZuploaderImage } from 'zuploader/types/zuploader';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Layout, Variant, Wrapper, CustomLayer, Header, Icon, Loading, LoadingLine } from 'sunzi-components';
import { useI18n } from 'sunzi-hooks';
import { matomoEvent, I18n, Qiniu, Image, loadFont, Device } from 'sunzi-utils';
import { Shop } from '../../type';
import { SizeData, ProductStyle, LetterHollowData, LetterHollowLayout, Output } from './letter-hollow';
import { ReactComponent as ArrowLeft } from './assets/arrow-left.svg';
import en_US from './locals/en_US.json';
import Photo from './component/photo';
import Tutorial from './component/tutorial'
import styles from './index.less';

const loadImageFromUrl = (url: string): Promise<fabric.Image> =>
  new Promise(resolve => {
    fabric.Image.fromURL(url, image => resolve(image), { crossOrigin: 'anonymous' });
  });

/** canvas 导出 blob */
const canvasToBlob = (canvas: HTMLCanvasElement, type?: string) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject();
    }, type);
  });

/** 编辑状态 */
enum EditType {
  /** 剪影文字编辑 */
  HollowText,
  /** 照片编辑 */
  Photo,
  /** 文字编辑 */
  LineText
}

const eventCategory = 'sunzi-letter-hollow';

interface LetterHollowProps {
  /**
   * @description 店铺信息
   */
  shop: Shop;
  /**
   * @description 尺寸信息
   */
  sizeData: SizeData[];
  /**
   * @description 产品样式
   */
  productStyle: ProductStyle[];
  /**
   * @description 图片数据
   */
  letterHollowData: LetterHollowData[];
  /**
   * @description 变体变化监听回调
   */
  onVariantChange(size: SizeData): void;
  /**
   * @description tracker
   */
  tracker?: MatomoTracker;
  /**
   * @description 确认回调
   */
  onConfirm(output: Output): void;
}

const LetterHollow: React.FC<LetterHollowProps> = ({
  shop,
  sizeData,
  productStyle,
  letterHollowData,
  onVariantChange,
  tracker,
  onConfirm
}) => {
  useI18n(shop.language, { en_US });

  /** 分类列表 */
  const sortOptions = useRef<{ [sortName: string]: LetterHollowLayout[] }>({});
  /** 用户图片 */
  const images = useRef<(string | null)[]>([]);
  /** 是否添加照片 */
  const _isAddPhoto = useRef<boolean>(false);
  /** 预览窗口 DOM */
  const _preview = useRef<HTMLDivElement>(null);
  /** canvas DOM */
  const _element = useRef<HTMLCanvasElement>(null);
  /** fabric canvas */
  const _canvas = useRef<fabric.Canvas>();
  /** 镂空刻字对象 */
  const _hollowText = useRef<fabric.IText>();
  /** 刻字对象 */
  const _lineText = useRef<fabric.Textbox>();
  /** 添加照片下标 */
  const _addImageIndex = useRef<number>(-1);
  /** 图片坐标 */
  const _imageOffset = useRef<({ left: number; top: number })[]>(Array(2).fill(null));
  /** 当前激活的尺寸 */
  const [currentSize, setCurrentSize] = useState<SizeData>(sizeData[0]);
  /** 定制层显隐 */
  const [customLayerVisible, setCustomLayerVisible] = useState<boolean>(false);
  /** 当前激活的分类 */
  const [currentSort, setCurrentSort] = useState<string>('All');
  /** 当前选择的模板 */
  const [currentLayout, setCurrentLayout] = useState<LetterHollowLayout>();
  /** 当前选择的风格 */
  const [currentStyle, setCurrentStyle] = useState<ProductStyle>(productStyle[0]);
  /** Zuploader组件显影 */
  const [zuploaderVisible, setZuploaderVisible] = useState<boolean>(false);
  /** Zuploader 是否单选上传 */
  const [isSingleUpload, setIsSingleUpload] = useState<boolean>(false);
  /** 预览显影 */
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  /** 预览加载 */
  const [previewLoading, setPreviewLoading] = useState<boolean>(true);
  /** 教程显影 */
  const [tutorialVisible, setTutorialVisible] = useState<boolean>(false);
  /** 镂空字体 */
  const [hollowTextValue, setHollowTextValue] = useState<string>('');
  /** 刻字 */
  const [lineTextValue, setLineTextValue] = useState<string>('');
  /** 输入状态 */
  const [isEditing, setIsEditing] = useState<boolean>(false);
  /** 编辑状态 */
  const [editType, setEditType] = useState<EditType>(EditType.HollowText);
  /** 加车 loading */
  const [uploading, setUploading] = useState<boolean>(false);
  /** forceUpdate */
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  /** dnd backend */
  const backend = Device.type() === 'mobile' ? TouchBackend : HTML5Backend;

  useEffect(() => {
    tracker?.trackEvent({
      action: matomoEvent.mount,
      category: eventCategory
    });
    createSortList();
  }, []);

  useEffect(() => {
    onVariantChange(currentSize);
    tracker?.trackEvent({
      category: eventCategory,
      action: 'event_tap_variant'
    });
  }, [currentSize]);

  useEffect(() => {
    if (previewVisible === true) {
      previewInit();
    }
  }, [previewVisible]);

  useEffect(() => {
    if (_canvas.current && _lineText.current && _hollowText.current) {
      if (isEditing) {
        if (editType === EditType.LineText) {
          _lineText.current.set('borderColor', '#ff533a');
          _canvas.current.setActiveObject(_lineText.current);
        } else if (editType === EditType.HollowText) {
          _hollowText.current.set('borderColor', '#ff533a');
          _canvas.current.setActiveObject(_hollowText.current);
        }
      } else {
        _canvas.current.discardActiveObject();
        _hollowText.current.exitEditing();
        _lineText.current.exitEditing();
      }
      _canvas.current.renderAll();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!customLayerVisible) {
      _isAddPhoto.current = false;
      images.current = [];
    }
  }, [customLayerVisible]);

  /** 构建分类列表 */
  const createSortList = () => {
    // 构建全部列表
    const all: { [id: string]: LetterHollowLayout } = {};
    sortOptions.current['All'] = [];
    letterHollowData.forEach(item => {
      const key = `${item.name}-${item.id}`;
      sortOptions.current[key] = [];
      item.layouts.forEach(layout => {
        if (!all.hasOwnProperty(layout.id)) {
          all[layout.id] = layout;
          sortOptions.current[key].push(layout);
          sortOptions.current['All'].push(layout);
        } else {
          sortOptions.current[key].push(layout);
        }
      });
    });
    // 当选项只有一个的时候，则不选择全部
    const _sortNames = Object.keys(sortOptions.current);
    let selectedName = 'All';
    if (_sortNames.length === 2) {
      selectedName = _sortNames.filter(item => item !== selectedName)[0];
    }
    setCurrentSort(selectedName);
  };

  /** 预览初始化 */
  const previewInit = () => {
    if (_element.current && _preview.current) {
      const previewWidth = _preview.current.offsetWidth;
      const previewHeight = _preview.current.offsetHeight;
      const canvasWidth = previewWidth * 0.8;
      const canvasHeight = previewHeight * 0.6;
      const widthScale = canvasWidth / currentSize.makeWidth;
      const heightScale = canvasHeight / currentSize.makeHeight;
      setPreviewLoading(true);
      _canvas.current = new fabric.Canvas(_element.current, {
        selection: false
      });
      if (widthScale < heightScale) {
        _canvas.current.setDimensions({
          width: canvasWidth,
          height: (currentSize.makeHeight / currentSize.makeWidth) * canvasWidth
        });
        _canvas.current.setDimensions(
          {
            width: canvasWidth,
            height: (currentSize.makeHeight / currentSize.makeWidth) * canvasWidth
          },
          { backstoreOnly: true }
        );
      } else {
        _canvas.current.setDimensions({
          width: (currentSize.makeWidth / currentSize.makeHeight) * canvasHeight,
          height: canvasHeight
        });
        _canvas.current.setDimensions(
          {
            width: (currentSize.makeWidth / currentSize.makeHeight) * canvasHeight,
            height: canvasHeight
          },
          { backstoreOnly: true }
        );
      }
      previewRender();
    }
  };

  /** 预览渲染 */
  const previewRender = async () => {
    if (_canvas.current && _element.current && images.current && currentLayout) {
      setPreviewLoading(true);
      _canvas.current.clear();
      const canvasHeight = _canvas.current.getHeight();
      const canvasWidth = _canvas.current.getWidth();
      // const { bottomImage } = currentLayout;
      const { hollowText, bottomImage, lineText } = currentLayout.sizeDatas[currentSize.id];
      const previewScale = canvasHeight / currentSize.makeHeight;

      const requestQueue: any[] = [loadImageFromUrl(bottomImage), loadFont(hollowText.font.name)];
      if (lineText) {
        loadFont(lineText.font.name);
      }
      const [_bottomImage] = await Promise.all(requestQueue);
      // 渲染背景图
      const _contentScale = Math.max(canvasWidth / _bottomImage.width!, canvasHeight / _bottomImage.height!);
      _bottomImage
        .set({
          top: canvasHeight / 2,
          left: canvasWidth / 2,
          originX: 'center',
          originY: 'center',
          hasControls: false,
          selectable: false,
          hoverCursor: 'default'
        })
        .scale(_contentScale);
      _canvas.current.add(_bottomImage);

      // 生成镂空刻字部分
      const _hollowTextLeft = (hollowText.left + hollowText.width / 2) * previewScale;
      const _hollowTextTop = (hollowText.top + hollowText.height / 2) * previewScale;
      const _hollowTextWidth = hollowText.width * previewScale;
      const _hollowTextHeight = hollowText.height * previewScale;
      _hollowText.current = new fabric.IText(hollowTextValue, {
        top: _hollowTextTop,
        left: _hollowTextLeft,
        fontFamily: hollowText.font.name,
        fontSize: hollowText.fontSize * previewScale,
        stroke: '#000',
        strokeWidth: 2,
        paintFirst: 'stroke',
        fill: '#fff',
        originX: 'center',
        originY: 'center',
        hoverCursor: 'default',
        hasControls: false,
        selectable: false,
        editable: false,
        lockMovementX: true,
        lockMovementY: true,
        padding: 5,
        borderColor: '#ff533a',
        borderDashArray: [2, 2]
      });
      const _hollowTextScale = Math.min(
        Math.min(
          _hollowTextWidth / (_hollowText.current.width || 1),
          _hollowTextHeight / (_hollowText.current.height || 1)
        ),
        1
      );
      _hollowText.current.scale(_hollowTextScale);
      _canvas.current.add(_hollowText.current);

      // 生成刻字部分
      if (lineText) {
        const _lineTextLeft = (lineText.left + lineText.width / 2) * previewScale;
        const _lineTextTop = (lineText.top + lineText.height / 2) * previewScale;
        const _lineTextWidth = lineText.width * previewScale;
        const _lineTextHeight = lineText.height * previewScale;
        _lineText.current = new fabric.Textbox(currentLayout.defaultText, {
          top: _lineTextTop,
          left: _lineTextLeft,
          width: _lineTextWidth,
          height: _lineTextHeight,
          fontFamily: lineText.font.name,
          fontSize: lineText.fontSize * previewScale,
          fill: lineText.fontColor,
          originX: 'center',
          originY: 'center',
          textAlign: 'center',
          hoverCursor: 'default',
          hasControls: false,
          selectable: false,
          editable: false,
          lockMovementX: true,
          lockMovementY: true,
          padding: 5,
          borderColor: '#ff533a',
          editingBorderColor: '#ff533a',
          borderDashArray: [2, 2],
          cursorColor: '#3c67ab'
        });
        const _lineTextScale = Math.min(_lineTextHeight / (_lineText.current.height || 1), 1);
        _lineText.current.scale(_lineTextScale);
        _canvas.current.add(_lineText.current);
      }

      // 清除字体缓存
      fabric.util.clearFabricFontCache();

      _canvas.current.renderAll();
      setPreviewLoading(false);
    }
  };

  /** 渲染图片 */
  const imageRender = async () => {
    if (_canvas.current && _hollowText.current) {
      // setPreviewLoading(true);
      const _images = await Promise.all(
        images.current.map(async item => {
          if (item) {
            return Image.loadImage(item);
          } else {
            return null;
          }
        })
      );
      const _usefulImages = _images.filter(item => item);
      const imageLenght = _hollowText.current.text!.length;

      for (let i = 0; i < imageLenght; i++) {
        const charBound = _hollowText.current.__charBounds![0][i];
        // const index = i % images.current.length;
        const _image = _images[i] || (_usefulImages[0] as HTMLImageElement);
        // @ts-ignore
        const scale = fabric.util.findScaleToCover(
          { width: _image.width, height: _image.height },
          { width: charBound.width, height: charBound.height }
        );
        const transformX = charBound.left - (_image.width * scale) / 2 + charBound.width / 2;
        const transformY = -((_image.height * scale) / 2) + (charBound.height as number) / 2;
        const image = new fabric.Pattern({
          source: _image,
          repeat: 'no-repeat',
          patternTransform: [scale, 0, 0, scale, transformX, transformY]
        });
        _hollowText.current.setSelectionStyles({ fill: image }, i, i + 1);
      }
      _canvas.current.renderAll();
      // setPreviewLoading(false);
    }
  };

  /** 关闭预览界面 */
  const handlePreviewClose = () => {
    if (editType === EditType.LineText) {
      // 返回到添加图片的页面
      setEditType(EditType.Photo);
    } else if (editType === EditType.Photo) {
      setIsSingleUpload(false);
      // 返回到编辑镂空字体页面
      setEditType(EditType.HollowText);
      // 清除镂空文字的图片
      _hollowText.current?.removeStyle('fill');
      _canvas.current?.renderAll();
    } else {
      setPreviewVisible(false);
    }
  };

  /** zuploader 确定 */
  const handleZuploaderConfirm = (output: ZuploaderImage[]) => {
    if (_isAddPhoto.current) {
      // images.current = images.current.concat(output.map(item => item.objectURL));
      images.current[_addImageIndex.current] = output[0].objectURL;
    } else {
      // 首次添加图片
      if (_hollowText.current) {
        const imageLength = _hollowText.current.text!.length;
        const _images = output.slice(0, imageLength);
        images.current = _images.map(item => item.objectURL);

        const fillLength = imageLength - images.current.length;
        images.current = images.current.concat(Array(fillLength).fill(null));
        setEditType(EditType.Photo);
        // 如果用户第一次登陆显示编辑引导
        setTutorialVisible(!!parseInt(window.localStorage.getItem('_sunzi_letter_hollow_tutorial') || '1'));
        window.localStorage.setItem('_sunzi_letter_hollow_tutorial', '0');
      }
    }
    imageRender();
  };

  /** 模板点击 */
  const handleTemplateSelected = (layout: LetterHollowLayout) => {
    setCurrentLayout(layout);
    setHollowTextValue(layout.defaultHollowText);
    setTimeout(() => setPreviewVisible(true), 300);
  };

  /** 镂空字体输入 */
  const handleHollowTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const canvasHeight = _canvas.current!.getHeight();
    const previewScale = canvasHeight / currentSize.makeHeight;
    const hollowTextData = currentLayout!.sizeDatas[currentSize.id].hollowText;
    if (_hollowText.current && _canvas.current && value.length <= hollowTextData.maxLength) {
      _hollowText.current.set('text', value);
      const _hollowTextScale = Math.min(
        Math.min(
          (hollowTextData.width * previewScale) / (_hollowText.current.width || 1),
          (hollowTextData.height * previewScale) / (_hollowText.current.height || 1)
        ),
        1
      );
      // console.log('height', _hollowText.current.getScaledHeight() / previewScale);
      _hollowText.current.scale(_hollowTextScale);
      setHollowTextValue(value);
      _canvas.current.renderAll();
    }
  };

  /** 编辑镂空刻字确定 */
  const handleEditHollowTextConfirm = () => {
    _isAddPhoto.current = false;
    setZuploaderVisible(true);
    // setEditType(EditType.Photo)
  };

  /** 添加图片 */
  const handleAddPhoto = (index: number) => {
    _isAddPhoto.current = true;
    _addImageIndex.current = index;
    setIsSingleUpload(true);
    setZuploaderVisible(true);
  };

  /** 删除图片 */
  const handleDeletePhoto = (index: number) => {
    images.current[index] = null;
    forceUpdate();
    imageRender();
  };

  /** 拖拽图片 */
  const handleDragPhoto = (oldIndex: number, newIndex: number) => {
    images.current.splice(oldIndex, 1, ...images.current.splice(newIndex, 1, images.current[oldIndex]));
    // setImages(imagesTmp);
    forceUpdate();
    imageRender();
  }

  /** 照片编辑点击确认 */
  const handleEditPhotoConfirm = () => {
    if (currentLayout!.sizeDatas[currentSize.id].lineText) {
      setEditType(EditType.LineText);
    } else {
      handleConfirm();
    }
  };

  /** 刻字字体输入 */
  const handleLineTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const lineTextData = currentLayout!.sizeDatas[currentSize.id].lineText;
    if (lineTextData && _lineText.current && _canvas.current) {
      const value = event.target.value;
      const canvasHeight = _canvas.current!.getHeight();
      const previewScale = canvasHeight / currentSize.makeHeight;
      if (value.length <= lineTextData.maxLength) {
        _lineText.current.set('text', value);
        const scale = Math.min((lineTextData.height * previewScale) / (_lineText.current.height || 1), 1);
        _lineText.current.scale(scale);
        setLineTextValue(value);
        _canvas.current.renderAll();
      }
    }
  };

  /** 整理图片链接顺序 */
  const sortOutImageUrl = (imagesUrl: string[]) => {
    const result = [];

    let index = 0;
    for (let i = 0; i < images.current.length; i++) {
      const image = images.current[i];
      if (image) {
        result.push(imagesUrl[index]);
        index += 1;
      } else {
        result.push(imagesUrl[0]);
      }
    }
    return result;
  }

  /** 确定加车 */
  const handleConfirm = async () => {
    if (images.current.length === 0 && !_hollowText.current?.text && !_lineText.current?.text) {
      return;
    }
    try {
      setUploading(true);
      const _usefulImage = images.current.filter(item => item) as string[];
      const blobImageQueue: any[] = [];
      for (let i = 0; i < _usefulImage.length; i++) {
        const sunziImage = new Image();
        const image = _usefulImage[i];
        blobImageQueue.push(sunziImage.load(image).compose(600).toBlob('png'));
      }
      blobImageQueue.push(canvasToBlob(_canvas.current!.getElement()));
      const blobImages = await Promise.all(blobImageQueue);
      const response = await new Qiniu().prefix('letter-hollow').timeout(30).retryCount(3).upload(blobImages);
      if (!response.status) {
        throw Error;
      }
      const preview = response.data.pop() as string;
      const imagesUrl = response.data as string[];
      // 整理上传图片顺序
      const sortImagesUrl = sortOutImageUrl(imagesUrl);
      // 获取布局数据
      const { hollowText, bottomImage, lineText } = currentLayout!.sizeDatas[currentSize.id];
      const canvasHeight = _canvas.current!.getHeight();
      const scale = canvasHeight / currentSize.makeHeight;
      const hollowTextCoord = [];
      for (let i = 0; i < _hollowText!.current!.text!.length; i++) {
        const char = _hollowText.current!.__charBounds![0][i];
        let coord = {
          left: char.left / scale,
          top: (char.deltaY as number) / scale,
          width: char.width / scale,
          height: (char.height as number) / scale,
          image: sortImagesUrl[i],
        };
        hollowTextCoord.push(coord);
      }
      // 镂空字体左上坐标
      const hollowTextTopLeft = _hollowText.current!._getLeftTopCoords();

      const output: Output = {
        size: currentSize,
        preview: preview,
        // images: imagesUrl,
        bottomImage: bottomImage,
        hollowText: {
          left: hollowTextTopLeft.x / scale,
          top: hollowTextTopLeft.y / scale,
          width: (_hollowText.current!.width as number) / scale,
          height: (_hollowText.current!.height as number) / scale,
          font: hollowText.font,
          fontSize: (_hollowText.current!.fontSize as number) / scale,
          text: hollowTextValue,
          textCoord: hollowTextCoord
        }
      };

      // 如果有刻字，就加上刻字数据
      if (lineText) {
        // 刻字左上坐标
        const letterTopLeft = _lineText.current!._getLeftTopCoords();
        output.lineText = {
          left: letterTopLeft.x / scale,
          top: letterTopLeft.y / scale,
          width: (_lineText.current!.width as number) / scale,
          height: (_lineText.current!.height as number) / scale,
          font: lineText.font,
          fontSize: (_lineText.current!.fontSize as number) / scale,
          fontColor: lineText.fontColor,
          text: lineTextValue
        };
      }

      tracker?.trackEvent({
        category: eventCategory,
        action: matomoEvent.tapConfirm
      });
      onConfirm(output);
      setUploading(false);
      handleClose();
    } catch {
      alert(I18n.format('add.cart.error'));
      setUploading(false);
    }
  };

  /** 关闭 */
  const handleClose = () => {
    const _sortNames = Object.keys(sortOptions.current);
    let selectedName = 'All';
    if (_sortNames.length === 2) {
      selectedName = _sortNames.filter(item => item !== selectedName)[0];
    }
    setIsSingleUpload(false);
    setCurrentSort(selectedName);
    setPreviewVisible(false);
    setCurrentLayout(undefined);
    setHollowTextValue('');
    setLineTextValue('');
    setIsEditing(false);
    setEditType(EditType.HollowText);
    setCustomLayerVisible(false);
    // _isAddPhoto.current = false;
    // images.current = [];
  };

  /** 渲染编辑部分 */
  const renderEdit = () => {
    switch (editType) {
      case EditType.HollowText:
        return (
          <div className={styles.editHollowTextWrapper}>
            <div className={styles.editTitle}>{`— ${I18n.format('edit.title')} —`}</div>
            <div className={styles.editInputWrapper}>
              <input
                type="text"
                value={hollowTextValue}
                onChange={handleHollowTextChange}
                onFocus={() => setIsEditing(true)}
                onBlur={() => setIsEditing(false)}
              />
              <div className={styles.inputCount}>
                <span className={styles.currentLength}>{hollowTextValue.length}</span>
                <span className={styles.maxLength}>
                  /{currentLayout!.sizeDatas[currentSize.id].hollowText.maxLength}
                </span>
              </div>
            </div>
            <div
              className={classnames(styles.confirm, styles.button, {
                [styles.disable]: !hollowTextValue
              })}
              onClick={handleEditHollowTextConfirm}
            >
              <span>{I18n.format('next')}</span>
            </div>
          </div>
        );
      case EditType.Photo:
        return (
          <div className={styles.editPhotoWrapper}>
            <div className={styles.editTitle}>{`— ${I18n.format('edit.photo.title')} —`}</div>
            <DndProvider backend={backend}>
              <div className={styles.editPhotoGroup}>
                {images.current.map((item, index) => {
                  const showDelete = images.current.filter(item => item).length > 1;
                  return (
                    <Photo
                      key={index}
                      currentIndex={index}
                      image={item}
                      text={hollowTextValue[index]}
                      showDelete={showDelete}
                      imageOffset={_imageOffset.current}
                      handleAddPhoto={() => handleAddPhoto(index)}
                      handleDeletePhoto={() => handleDeletePhoto(index)}
                      handleDragPhoto={handleDragPhoto}
                    />
                  );
                })}
              </div>
            </DndProvider>
            <div
              className={classnames(styles.button, styles.confirm, {
                [styles.buttonDisable]: previewLoading
              })}
              onClick={handleEditPhotoConfirm}
            >
              <span>
                {currentLayout!.sizeDatas[currentSize.id].lineText ? I18n.format('next') : I18n.format('add.cart')}
              </span>
            </div>
          </div>
        );
      case EditType.LineText:
        return (
          <div className={classnames(styles.editLineTextWrapper, styles.editHollowTextWrapper)}>
            <div className={styles.editTitle}>{`— ${I18n.format('edit.title')} —`}</div>
            <div className={styles.editInputWrapper}>
              <input
                type="text"
                value={lineTextValue}
                onChange={handleLineTextChange}
                onFocus={() => setIsEditing(true)}
                onBlur={() => setIsEditing(false)}
              />
              <div className={styles.inputCount}>
                <span className={styles.currentLength}>{lineTextValue.length}</span>
                <span className={styles.maxLength}>
                  /{currentLayout!.sizeDatas[currentSize.id].lineText!.maxLength}
                </span>
              </div>
            </div>
            <div
              className={classnames(styles.confirm, styles.button, {
                [styles.disable]: !lineTextValue
              })}
              onClick={handleConfirm}
            >
              <span>{I18n.format('add.cart')}</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const map = new Map();
  const innerFonts = letterHollowData.map(item => {
    return item.layouts.map(layout => {
      return Object.keys(layout.sizeDatas).map(sizeId => {
        const sizeData = layout.sizeDatas[sizeId];
        let result = '';
        if (sizeData.lineText && !map.has(sizeData.lineText.font.name)) {
          map.set(sizeData.lineText.font.name, 1);
          result += `
            @font-face {
              font-weight: normal;
              font-style: normal;
              font-family: ${sizeData.lineText.font.name};
              src: url('${sizeData.lineText.font.url}');
            }
          `;
        }
        if (!map.has(sizeData.hollowText.font.name)) {
          map.set(sizeData.hollowText.font.name, 1);
          result += `
            @font-face {
              font-weight: normal;
              font-style: normal;
              font-family: ${sizeData.hollowText.font.name};
              src: url('${sizeData.hollowText.font.url}');
            }
          `;
        }
        return result;
      });
    });
  });

  return (
    <Layout className="sunzi-letter-hollow">
      <style>{innerFonts}</style>
      {productStyle.map(item => (
        <Fragment key={item.id}>
          <link rel="preload" href={item.selectedAlbum} as="image" />
          <link rel="preload" href={item.unSelectedAlbum} as="image" />
        </Fragment>
      ))}
      <Variant title={I18n.format('variant.size')} className={styles.variant}>
        {sizeData.map((item, index) => (
          <Variant.Tag
            key={index}
            className={styles.variantTag}
            active={currentSize.id === item.id}
            title={item.name}
            onClick={() => setCurrentSize(item)}
          />
        ))}
      </Variant>
      <Variant title={I18n.format('variant.style')} className={styles.variant}>
        {productStyle.map(item => (
          <div
            key={item.id}
            className={classnames(styles.style, {
              [styles.active]: item.id === currentStyle.id
            })}
            onClick={() => setCurrentStyle(item)}
          >
            <div className={styles.variantContent}>
              <div
                className={styles.album}
                style={{
                  backgroundImage: `url(${item.id === currentStyle.id ? item.selectedAlbum : item.unSelectedAlbum})`
                }}
              />
            </div>
            <div className={styles.styleName}>{item.name}</div>
          </div>
        ))}
      </Variant>
      <button type="button" className={styles.buttonCustom} onClick={() => setCustomLayerVisible(true)}>
        {I18n.format('customize.now')}
      </button>
      {customLayerVisible && (
        <CustomLayer className={styles.customLayer} tracker={tracker} eventCategory={eventCategory}>
          <Header
            className={styles.header}
            renderCloseContent={
              <Icon className={styles.iconWrapper}>
                <ArrowLeft />
              </Icon>
            }
            renderContent={<div className={styles.headerContent}>{I18n.format('choose.title')}</div>}
            onClose={handleClose}
          />
          <div className={styles.sortOptions}>
            {Object.keys(sortOptions.current).map((item, index) => {
              // 当前只有一个分类的情况下，就不显示全部选项
              if (item === 'All' && Object.keys(sortOptions.current).length === 2) {
                return null;
              }
              return (
                <div key={index} className={styles.sortOptionWrap}>
                  <div
                    className={classnames(styles.sortOption, {
                      [styles.sortOptionSelected]: currentSort === item
                    })}
                    onClick={() => setCurrentSort(item)}
                  >
                    {item.split('-')[0]}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.templateOptions}>
            {sortOptions.current[currentSort].map((item, index) => (
              <div
                key={index}
                className={classnames(styles.templateOptionWrap, {
                  [styles.templateSelected]: item.id === currentLayout?.id
                })}
              >
                <div
                  className={styles.templateAlbum}
                  style={{
                    paddingTop: `${(currentSize.makeHeight / currentSize.makeWidth) * 100}%`,
                    backgroundImage: `url(${Qiniu.thumbnail(item.album, { height: 400 })})`
                  }}
                  onClick={() => handleTemplateSelected(item)}
                />
              </div>
            ))}
          </div>
          {previewVisible && (
            <Wrapper className={styles.previewWrapper}>
              <div className={styles.previousWrapper}>
                <div className={styles.previous} onClick={handlePreviewClose}>
                  <Icon className={styles.iconWrapper}>
                    <ArrowLeft />
                  </Icon>
                </div>
              </div>
              <div ref={_preview} className={styles.preview}>
                <div className={styles.previewContent}>
                  <div className={styles.canvasWrapper}>
                    <canvas ref={_element} />
                    {previewLoading && (
                      <Wrapper className={styles.loadingWrapper}>
                        <Loading size={45} />
                        <span>{I18n.format('loading')}...</span>
                      </Wrapper>
                    )}
                  </div>
                </div>
                <div className={styles.editWrapper}>{renderEdit()}</div>
              </div>
              {tutorialVisible &&
                <Tutorial
                  imageOffset={_imageOffset.current}
                  images={images.current}
                  onConfirm={() => setTutorialVisible(false)}
                />
              }
            </Wrapper>
          )}
          {zuploaderVisible && (
            <Wrapper className={styles.zuploaderWrapper}>
              <Zuploader
                multiple={!isSingleUpload}
                lowQualityThreshold={0}
                local={shop.language}
                onClose={() => setZuploaderVisible(false)}
                onConfirm={handleZuploaderConfirm}
              />
            </Wrapper>
          )}
          {uploading && (
            <Wrapper className={styles.uploadWrapper}>
              <LoadingLine />
            </Wrapper>
          )}
        </CustomLayer>
      )}
    </Layout>
  );
};

/** 组件渲染 */
export { render } from 'sunzi-utils';

export default LetterHollow;
